import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase/config";
import { collection, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { getServiceAvailabilityLabel } from "../../constants/availability";
import { getProfilePath } from "../../utils/storefront";
import {
  ADDITIONAL_PROFILE_ROLE_OPTIONS,
  getProfileRoleLabel,
} from "../../utils/profileRoles";
import FarmAvatar from "../Common/FarmAvatar";

const ROLE_OPTIONS = [
  { value: "all", label: "All", emoji: "🌎" },
  { value: "landowner", label: "Land Owners", emoji: "🌾" },
  { value: "farmer", label: "Farmers", emoji: "🚜" },
  { value: "homesteader", label: "Homesteaders", emoji: "🏡" },
  { value: "day_worker", label: "Day Workers", emoji: "🛠" },
  { value: "community", label: "Community", emoji: "🤝" },
];

ROLE_OPTIONS.push(...ADDITIONAL_PROFILE_ROLE_OPTIONS);

const getUserRoles = (user) => {
  if (Array.isArray(user?.roles) && user.roles.length > 0) return user.roles;
  if (user?.type) return [user.type];
  return ["community"];
};

const formatRole = (role) => {
  const map = {
    landowner: "🌾 Land Owner",
    farmer: "🚜 Farmer",
    homesteader: "🏡 Homesteader",
    day_worker: "🛠 Day Worker",
    community: "🤝 Community Member",
    operator: "🛠 Operator",
    event_planner: "🎪 Event Planner",
  };

  return map[role] || getProfileRoleLabel(role);
};

const isVisibleUser = (user) => {
  if (!user) return false;

  const status = user.accountStatus || "active";

  if (status === "deactivated") return false;
  if (status === "deleted") return false;
  if (user.isPublic === false) return false;

  return true;
};

const getLocationLabel = (user) => {
  return (
    user?.location?.locationText ||
    user?.locationText ||
    [user?.location?.city || user?.city, user?.location?.state || user?.state]
      .filter(Boolean)
      .join(", ") ||
    user?.location?.county ||
    user?.county ||
    ""
  );
};

const BrowseUsers = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snapshot) => {
      const list = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter(isVisibleUser)
        .sort((a, b) => {
          const aName = (a.name || "").toLowerCase();
          const bName = (b.name || "").toLowerCase();
          return aName.localeCompare(bName);
        });

      setUsers(list);
    });

    return () => unsub();
  }, []);

  const stats = useMemo(() => {
    const activeFounders = users.filter(
      (user) => user.founder === true && user.founderSpotActive !== false
    ).length;

    return {
      totalUsers: users.length,
      activeFounders,
    };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return users.filter((user) => {
      const roles = getUserRoles(user);

      const matchesRole =
        roleFilter === "all" || roles.some((role) => role === roleFilter);

      if (!matchesRole) return false;

      if (!searchText) return true;

      const nameMatch = (user.name || "").toLowerCase().includes(searchText);
      const bioMatch = (user.bio || "").toLowerCase().includes(searchText);
      const introMatch = (user.intro || "").toLowerCase().includes(searchText);
      const locationMatch = getLocationLabel(user)
        .toLowerCase()
        .includes(searchText);

      const roleMatch = roles.some((role) => {
        const safeRole = String(role || "").toLowerCase();
        const formattedRole = formatRole(role).toLowerCase();

        return (
          safeRole.includes(searchText) ||
          formattedRole.includes(searchText)
        );
      });

      return nameMatch || bioMatch || introMatch || roleMatch || locationMatch;
    });
  }, [users, search, roleFilter]);

  return (
    <div className="browse-users-page page-shell page-shell-wide">
      <section className="browse-users-hero">
        <div>
          <span className="browse-users-kicker">🌾 Local Network</span>
          <h2>Browse People</h2>
          <p>
            Find farmers, land owners, homesteaders, day workers, and community
            members near you.
          </p>
        </div>

        <div className="browse-users-stats">
          <span>
            Active users <strong>{stats.totalUsers}</strong>
          </span>

          <span>
            Founders <strong>{stats.activeFounders}</strong>
          </span>
        </div>
      </section>

      <section className="browse-users-controls">
        <div className="browse-search-box">
          <span>🔍</span>
          <input
            type="text"
            placeholder="Search by name, role, intro, or bio..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="browse-role-filters">
          {ROLE_OPTIONS.map((role) => (
            <button
              key={role.value}
              type="button"
              className={roleFilter === role.value ? "active" : ""}
              onClick={() => setRoleFilter(role.value)}
            >
              <span>{role.emoji}</span>
              {role.label}
            </button>
          ))}
        </div>
      </section>

      <div className="browse-users-result-row">
        <p>
          Showing <strong>{filteredUsers.length}</strong> of{" "}
          <strong>{users.length}</strong> active profiles
        </p>

        {(search || roleFilter !== "all") && (
          <button
            type="button"
            className="btn-soft"
            onClick={() => {
              setSearch("");
              setRoleFilter("all");
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {filteredUsers.length === 0 ? (
        <div className="browse-empty-state">
          <div>🌱</div>
          <strong>No people found</strong>
          <p>Try clearing the filters or searching a different role.</p>
        </div>
      ) : (
        <div className="browse-user-grid">
          {filteredUsers.map((user) => {
            const displayName = user.name || "Unnamed User";
            const roles = getUserRoles(user);
            const rating = Number(user.rating || 0).toFixed(1);
            const reviewCount = Number(user.reviewCount || 0);
            const isFounder =
              user.founder === true && user.founderSpotActive !== false;
            const locationLabel = getLocationLabel(user);

            return (
              <article
                key={user.id}
                className="browse-user-card"
                onClick={() => navigate(getProfilePath(user))}
              >
                <div className="browse-user-cover">
                  <FarmAvatar
                    profile={{ ...user, name: displayName }}
                    className="browse-user-photo"
                  />

                  {isFounder && (
                    <span className="browse-founder-badge">
                      🌾 Founder
                    </span>
                  )}
                </div>

                <div className="browse-user-body">
                  <div className="browse-user-title-row">
                    <div>
                      <h3>{displayName}</h3>

                      {user.intro && (
                        <p className="browse-user-intro">{user.intro}</p>
                      )}
                    </div>

                    <span className="browse-rating-pill">
                      ⭐ {rating}
                      <small>({reviewCount})</small>
                    </span>
                  </div>

                  <div className="browse-role-list">
                    {roles.map((role) => (
                      <span key={role} className="browse-role-pill">
                        {formatRole(role)}
                      </span>
                    ))}
                    {(user.serviceAvailabilityStatus || "available") !== "available" && (
                      <span
                        className={`browse-role-pill availability-${
                          user.serviceAvailabilityStatus || "available"
                        }`}
                      >
                        {getServiceAvailabilityLabel(user.serviceAvailabilityStatus)}
                      </span>
                    )}
                  </div>

                  {(user.seasonalAvailabilityNote || user.availableStarting) && (
                    <p className="browse-user-availability">
                      {user.availableStarting
                        ? `Starting ${new Date(`${user.availableStarting}T00:00:00`).toLocaleDateString()}`
                        : ""}
                      {user.availableStarting && user.seasonalAvailabilityNote ? " - " : ""}
                      {user.seasonalAvailabilityNote || ""}
                    </p>
                  )}

                  {locationLabel && (
                    <p className="browse-user-location">{locationLabel}</p>
                  )}

                  <p className="browse-user-bio">
                    {user.bio
                      ? user.bio.length > 130
                        ? `${user.bio.slice(0, 130)}...`
                        : user.bio
                      : "No bio yet. Visit their profile to learn more."}
                  </p>

                  <div className="browse-user-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(getProfilePath(user));
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BrowseUsers;
